import { useEffect, useRef, useState } from "react";
import { UploadSimple, FileArrowUp, CheckCircle, Warning, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";

const FORM_LABELS = {
  profile: "Form 2 — Intake Assessment",
  gty: "Getting to Know You",
  medications: "Form 3 — Medication List",
  careplan: "Form 4 — Care Plan (draft)",
};

const humanize = (k) => k.replace(/_/g, " ");
const showVal = (v) => {
  if (v == null || v === "") return "—";
  if (typeof v === "object") {
    const s = JSON.stringify(v);
    return s.length > 160 ? s.slice(0, 160) + "…" : s;
  }
  return String(v);
};

export const ImportDialog = ({ clientId, client }) => {
  const [open, setOpen] = useState(false);
  const [live, setLive] = useState(client);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [included, setIncluded] = useState({});
  const pollRef = useRef(null);

  useEffect(() => { setLive(client); }, [client]);

  const status = live?.import_status || "idle";
  const draft = live?.import_draft || null;

  useEffect(() => {
    if (open && status === "processing") {
      pollRef.current = setInterval(async () => {
        try {
          const { data } = await api.get(`/clients/${clientId}`);
          setLive(data);
        } catch { /* keep polling */ }
      }, 3000);
      return () => clearInterval(pollRef.current);
    }
    clearInterval(pollRef.current);
  }, [open, status, clientId]);

  useEffect(() => {
    if (status === "done" && draft?.parsed) {
      const auto = {};
      for (const [formKey, fields] of Object.entries(draft.parsed)) {
        const current = client?.forms?.[formKey] || {};
        auto[formKey] = {};
        for (const [field, value] of Object.entries(fields)) {
          const existing = current[field];
          const differs = existing != null && existing !== "" && showVal(existing) !== showVal(value);
          auto[formKey][field] = !differs;
        }
      }
      setIncluded(auto);
    }
  }, [status, draft, client]);

  const upload = async () => {
    if (!file) { toast.error("Choose a file first"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/clients/${clientId}/import-document`, fd, { timeout: 60000 });
      toast.success("Document uploaded — AI is reading it");
      setLive({ ...live, import_status: "processing" });
      setFile(null);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const apply = async () => {
    const forms = {};
    for (const [formKey, fields] of Object.entries(included)) {
      const picked = {};
      for (const [field, use] of Object.entries(fields)) {
        if (use) picked[field] = draft.parsed[formKey][field];
      }
      if (Object.keys(picked).length) forms[formKey] = picked;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/clients/${clientId}/import-apply`, { forms });
      toast.success(`Imported into: ${data.applied.map((k) => FORM_LABELS[k] || k).join(", ")}`);
      setOpen(false);
      window.location.reload();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const discard = async () => {
    setBusy(true);
    try {
      await api.post(`/clients/${clientId}/import-discard`);
      toast.success("Import discarded");
      setOpen(false);
      window.location.reload();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const parsedForms = draft?.parsed ? Object.entries(draft.parsed) : [];
  const selectedCount = Object.values(included).reduce((n, f) => n + Object.values(f).filter(Boolean).length, 0);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="border-stone-300 text-stone-700 hover:bg-stone-900 hover:text-white" data-testid="import-dialog-button">
          <UploadSimple size={16} className="mr-1.5" /> Import Document
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="import-dialog">
        <DialogHeader>
          <DialogTitle>Import an existing document</DialogTitle>
          <DialogDescription>
            Upload an old care plan, medication list, assessment, or discharge summary (PDF, Word, Excel, or text). Identifiers are scrubbed before the AI reads it — it only ever sees the client code. You review everything before it's applied.
          </DialogDescription>
        </DialogHeader>

        {status !== "processing" && status !== "done" && (
          <div className="space-y-4" data-testid="import-upload-step">
            <label className="flex cursor-pointer flex-col items-center gap-2 rounded-lg border-2 border-dashed border-stone-300 bg-accent/40 p-8 text-center hover:border-primary">
              <FileArrowUp size={28} className="text-stone-400" />
              <span className="text-sm font-medium text-stone-700">{file ? file.name : "Choose a PDF, Word, Excel, or text file"}</span>
              <span className="text-xs text-stone-400">Scans/photos of paper aren't supported yet — the file needs real text</span>
              <input type="file" accept=".pdf,.docx,.xlsx,.txt" className="hidden" data-testid="import-file-input"
                onChange={(e) => setFile(e.target.files?.[0] || null)} />
            </label>
            {status === "error" && (
              <p className="rounded-lg border border-[#C0392B]/30 bg-[#C0392B]/5 p-3 text-sm text-[#C0392B]" data-testid="import-error">
                Last import failed: {live?.import_error || "unknown error"}
              </p>
            )}
            <Button onClick={upload} disabled={busy || !file} className="w-full bg-primary text-primary-foreground hover:bg-stone-900" data-testid="import-upload-button">
              {busy ? "Uploading..." : "Upload & Parse"}
            </Button>
          </div>
        )}

        {status === "processing" && (
          <div className="flex flex-col items-center gap-3 py-10" data-testid="import-processing">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            <p className="text-sm text-stone-500">Reading and mapping the document — usually under a minute…</p>
          </div>
        )}

        {status === "done" && draft?.parsed && (
          <div className="space-y-4" data-testid="import-review">
            {(draft.questions || []).length > 0 && (
              <div className="rounded-lg border border-[#B7791F]/30 bg-[#B7791F]/5 p-4" data-testid="import-questions">
                <p className="flex items-center gap-1.5 text-sm font-semibold text-[#8a5d14]"><Warning size={15} /> The AI flagged these — please resolve by hand:</p>
                <ul className="mt-2 list-inside list-disc space-y-1 text-xs text-stone-600">
                  {draft.questions.map((q) => <li key={q}>{q}</li>)}
                </ul>
              </div>
            )}
            {parsedForms.length === 0 && (
              <p className="py-6 text-center text-sm text-stone-500">Nothing mappable was found in that document.</p>
            )}
            {parsedForms.map(([formKey, fields]) => {
              const current = client?.forms?.[formKey] || {};
              return (
                <div key={formKey} className="rounded-lg border border-stone-200 bg-white" data-testid={`import-form-${formKey}`}>
                  <p className="border-b border-stone-100 px-4 py-2.5 font-heading text-sm font-semibold text-stone-900">{FORM_LABELS[formKey] || formKey}</p>
                  <div className="divide-y divide-stone-100">
                    {Object.entries(fields).map(([field, value]) => {
                      const existing = current[field];
                      const differs = existing != null && existing !== "" && showVal(existing) !== showVal(value);
                      const use = included[formKey]?.[field] ?? !differs;
                      return (
                        <label key={field} className="flex cursor-pointer items-start gap-3 px-4 py-2.5 text-sm hover:bg-accent/40" data-testid={`import-field-${formKey}-${field}`}>
                          <input type="checkbox" className="mt-1" checked={use} data-testid={`import-use-${formKey}-${field}`}
                            onChange={(e) => setIncluded((s) => ({ ...s, [formKey]: { ...(s[formKey] || {}), [field]: e.target.checked } }))} />
                          <span className="min-w-0 flex-1">
                            <span className="font-medium text-stone-800">{humanize(field)}</span>
                            <span className="block break-words text-xs text-stone-600">{showVal(value)}</span>
                            {differs && (
                              <span className="block text-xs text-[#B7791F]">Current form has: {showVal(existing)}</span>
                            )}
                          </span>
                          {differs && <span className="shrink-0 rounded-full bg-[#B7791F]/10 px-2 py-0.5 text-[10px] font-medium text-[#8a5d14]">conflict</span>}
                        </label>
                      );
                    })}
                  </div>
                </div>
              );
            })}
            <div className="flex items-center justify-between gap-3 pt-2">
              <Button variant="outline" onClick={discard} disabled={busy} className="border-stone-300 text-stone-500 hover:text-[#C0392B]" data-testid="import-discard-button">
                <X size={14} className="mr-1" /> Discard
              </Button>
              <Button onClick={apply} disabled={busy || selectedCount === 0} className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid="import-apply-button">
                {busy ? "Applying..." : `Apply ${selectedCount} field${selectedCount === 1 ? "" : "s"}`}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
